import { type RequestAttachment } from 'entities/Attachment/model/RequestAttachment'
import { type IFiltersListGroups } from 'entities/Contacts/api/types'
import type { IParamsSendFrom, IResponseSendFrom } from 'entities/SendFrom'
import type { IMergeFieldType } from 'entities/Integrations'
import { type Paths, type ToErrorList } from '../utils'

export enum ChatbotInstructionType {
  LeadQualification = 1,
  Custom = 2,
  FAQ = 3,
  BookAppointment = 4,
  Support = 5,
  Unified = 6,
}

export const ChatbotFollowUpAssertiveness = {
  Subtle: 'subtle',
  Steady: 'steady',
  Assertive: 'assertive',
} as const

export const ChatbotToneOfVoice = {
  Fun: 'fun',
  Friendly: 'friendly',
  Professional: 'professional',
} as const

export const ChatbotMessageLength = {
  Brief: 'brief',
  Standard: 'standard',
  Extended: 'extended',
} as const

export type IChatbotFollowUpAssertiveness =
  (typeof ChatbotFollowUpAssertiveness)[keyof typeof ChatbotFollowUpAssertiveness]

export type IChatbotToneOfVoice = (typeof ChatbotToneOfVoice)[keyof typeof ChatbotToneOfVoice]

export type IChatbotMessageLength = (typeof ChatbotMessageLength)[keyof typeof ChatbotMessageLength]

export type IChatbotSettings = {
  followup_window?: null | {
    value: number
  }
  enable_followup?: null | {
    value: boolean
  }
  followup_assertiveness?: null | {
    value: IChatbotFollowUpAssertiveness
  }
  max_attempts?: null | {
    value: number
  }
  instructions?: null | {
    value: string
  }
  use_knowledge_base?: null | {
    value: boolean
  }
  skip_known_fields?: null | {
    value: boolean
  }
  current_step?: null | {
    value: string
  }
  book_appointment_enabled?: null | {
    value: boolean
  }
  book_appointment_type?: null | {
    value: IBookingType
  }
  book_appointment_link?: null | {
    value: string
    id: string
    name: string
  }
  booking_provider?: null | {
    value: string
  }
  respond_tone?: null | {
    value: IChatbotToneOfVoice
  }
  respond_message_length?: null | {
    value: IChatbotMessageLength
  }
  complete_instructions?: null | {
    value: string
  }
  allow_re_entering?: null | {
    value: boolean
  }
  qualify_enabled?: null | {
    value: boolean
  }
  disqualify_behavior?: null | {
    value: ChatbotDisqualifyBehaviorStatus
  }
  qualify_instruction?: null | {
    value: string
  }
  data_collection_instruction?: null | {
    value: string
  }
  smart_booking_enabled?: null | {
    value: boolean
  }
  session_lifetime?: null | {
    value: number
  }
}

export enum ChatbotDisqualifyBehaviorStatus {
  Stop = 'stop',
  Continue = 'continue',
}

export type IChatbotField = {
  key: string
  name: string
  type: IMergeFieldType
  settings?: {
    default: boolean
    required: boolean
    choices: string[]
  }
}

export const ChatbotActionType = {
  AddTag: 1,
  RemoveTag: 2,
  ContactNote: 4,
  SendMessage: 200,
} as const

export type IChatbotActionType = (typeof ChatbotActionType)[keyof typeof ChatbotActionType]

export type ChatbotActionTypeMap = {
  [ChatbotActionType.AddTag]: IChatbotActionTag
  [ChatbotActionType.RemoveTag]: IChatbotActionTag
  [ChatbotActionType.ContactNote]: IChatbotActionNote
  [ChatbotActionType.SendMessage]: IChatbotActionSendMessage
}

export type IChatbotActionSetting<T extends IChatbotActionType> = {
  type: T
  settings: ChatbotActionTypeMap[T]
}

export type IChatbotActionTag = {
  tag_ids: number[]
}

export type IChatbotActionAddTagSetting = IChatbotActionSetting<typeof ChatbotActionType.AddTag>
export type IChatbotActionRemoveTagSetting = IChatbotActionSetting<
  typeof ChatbotActionType.RemoveTag
>

export type IChatbotActionNote = {
  note: string
}

export type IChatbotActionContactNoteSetting = IChatbotActionSetting<
  typeof ChatbotActionType.ContactNote
>

export type IChatbotActionSendMessage = IChatbotMessage

export type IChatbotActionSendMessageSetting = IChatbotActionSetting<
  typeof ChatbotActionType.SendMessage
>

export type IChatbotActionSettings =
  | IChatbotActionAddTagSetting
  | IChatbotActionRemoveTagSetting
  | IChatbotActionContactNoteSetting
  | IChatbotActionSendMessageSetting

export type IChatbotMessage = {
  type?: 'manual' | 'auto'
  message: string
  media_url: Omit<RequestAttachment, 'media_preview'>[]
}

export enum ChatbotStatus {
  Draft = 0,
  Active = 1,
  Disabled = 2,
  Invalid = 3,
}

export type IChatbotBase = {
  id: number
  name: string
  type: number
  is_active: boolean
  send_from: IResponseSendFrom | null
  organization_id: number
  status: ChatbotStatus
  created_at: string
  updated_at: string
  total_contacts_count: number
  terminated_contacts_count: number
  completed_contacts_count: number
  fallback_contacts_count: number
  in_progress_contacts_count: number
  qualified_contacts_count: number
  disqualified_contacts_count: number
  not_qualified_contacts_count: number
  cancelled_appointments_count: number
  rescheduled_appointments_count: number
  quiet_hours_messages_count?: number
  skipped_contacts_count: number
  filters: IChatbotFilters | null
  first_message: IChatbotMessage | null
}

export type IChatbotPayload = {
  type: ChatbotInstructionType
  name: string
  is_active: boolean
  send_from: IParamsSendFrom | null
  first_message?: IChatbotMessage | null
  filters: IChatbotFilters | null
  settings: IChatbotSettings | null
  fields: IChatbotField[]
  success_actions: IChatbotActionSettings[] | null
  fallback_actions: IChatbotActionSettings[] | null
  qualified_actions: IChatbotActionSettings[] | null
  disqualified_actions: IChatbotActionSettings[] | null
  knowledge_base_documents: number[] | null
  status?: ChatbotStatus | null
  is_live_document_issue?: boolean
}

export type IAgentGeneralPayload = {
  type: ChatbotInstructionType
  is_active: boolean
  settings: {
    current_step: IChatbotSettings['current_step']
  }
}

export type IAgentFirstStepPayload = {
  name: string
  first_message: IChatbotMessage | null
  settings: {
    use_knowledge_base: IChatbotSettings['use_knowledge_base'] | null
  }
  knowledge_base_documents: number[] | null
}

export type IAgentSecondStepPayload = {
  settings: {
    book_appointment_type: IChatbotSettings['book_appointment_type'] | null
    book_appointment_link: IChatbotSettings['book_appointment_link'] | null
    smart_booking_enabled: IChatbotSettings['smart_booking_enabled'] | null
    booking_provider: IChatbotSettings['booking_provider'] | null
  }
}

export type IAgentThirdStepPayload = {
  success_actions: IChatbotActionSettings[] | null
  fallback_actions: IChatbotActionSettings[] | null
  qualified_actions: IChatbotActionSettings[] | null
  disqualified_actions: IChatbotActionSettings[] | null
  fields: IChatbotField[]
  settings: {
    skip_known_fields: IChatbotSettings['skip_known_fields'] | null
    data_collection_instruction: IChatbotSettings['data_collection_instruction'] | null
    qualify_enabled: IChatbotSettings['qualify_enabled'] | null
    qualify_instruction: IChatbotSettings['qualify_instruction'] | null
    disqualify_behavior: IChatbotSettings['disqualify_behavior'] | null
  }
}

export type IAgentFourthStepPayload = {
  filters: IChatbotFilters | null
  send_from: IParamsSendFrom | null
  settings: {
    allow_re_entering: IChatbotSettings['allow_re_entering'] | null
  }
}

export type IAgentAdvancedGeneralPayload = {
  settings: {
    followup_window?: IChatbotSettings['followup_window'] | null
    enable_followup?: IChatbotSettings['enable_followup'] | null
    followup_assertiveness?: IChatbotSettings['followup_assertiveness'] | null
    respond_tone?: IChatbotSettings['respond_tone'] | null
    respond_message_length?: IChatbotSettings['respond_message_length'] | null
  }
}

export type IAgentAdvancedPromptPayload = {
  settings: {
    instructions?: IChatbotSettings['instructions'] | null
  }
}

export type IAgentPayload = {
  general: IAgentGeneralPayload
  step_1?: IAgentFirstStepPayload
  step_2?: IAgentSecondStepPayload
  step_3?: IAgentThirdStepPayload
  step_4?: IAgentFourthStepPayload
  advanced_general?: IAgentAdvancedGeneralPayload
  advanced_prompt?: IAgentAdvancedPromptPayload
}

type IChatbotPayloadKeysError = IChatbotPayload & {
  'settings.data_collection_instruction.merge_fields_min_1': string
  'settings.data_collection_instruction.merge_fields_max_10': string
}

export type IChatbotPayloadError = ToErrorList<IChatbotPayloadKeysError>
export type IChatbotPayloadPath = Paths<IChatbotPayloadKeysError>

export type IChatbotData = IChatbotPayload & IChatbotBase

export type IChatbotFilters = IFiltersListGroups

export const ChatbotConversationStatus = {
  InProgress: 'in_progress',
  Completed: 'completed',
  Qualified: 'qualified',
  Disqualified: 'disqualified',
  NotQualified: 'not_qualified',
  Canceled: 'appointment_cancelled',
  Rescheduled: 'appointment_rescheduled',
  Abandoned: 'abandoned',
  Terminated: 'terminated',
  QuietHours: 'quiet_hours',
  Skipped: 'skipped',
} as const

export type IChatbotConversationStatus =
  (typeof ChatbotConversationStatus)[keyof typeof ChatbotConversationStatus]

export type IResponseAnalyticContact = {
  id: number
  first_name: string
  last_name: string
  email: string | null
  number: string
  full_name: string
  photo_url?: string | null
}

export type IChatbotAnalyticBase = {
  id: number
  chatbot_id: number
  contact_id: number
  uuid: string
  created_at: string
  send_from?: IParamsSendFrom | null
  contact: IResponseAnalyticContact | null
  workflow_execution_id: string
  core_conversation_id: number
} & (
  | {
      status: typeof ChatbotConversationStatus.Abandoned
      success_at: null
      fallback_at: string
    }
  | {
      status: typeof ChatbotConversationStatus.Completed
      success_at: string
      fallback_at: null
    }
  | {
      status: typeof ChatbotConversationStatus.InProgress
      success_at: null
      fallback_at: null
    }
  | {
      status: typeof ChatbotConversationStatus.Terminated
      success_at: null
      fallback_at: null
    }
  | {
      status: typeof ChatbotConversationStatus.Skipped
      success_at: null
      fallback_at: null
    }
)

export type IChatbotAnalyticQuietHoursItem = {
  chatbot_id: number
  contact: IResponseAnalyticContact | null
  contact_id: number
  core_conversation_id: number
  number_id: number
  message_created_at: string
  message_send_at: string
}

export const ChatbotBookingType = {
  TimeChat: 'time_chat',
  ShareLink: 'share_link',
} as const

export type IBookingType = (typeof ChatbotBookingType)[keyof typeof ChatbotBookingType]
